// src/pages/ExtensionsPage.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Tooltip, CircularProgress,
  FormGroup, FormControlLabel, Checkbox, FormLabel, Select, MenuItem, FormControl, InputLabel,
  Stack, TableSortLabel, Menu, ButtonGroup
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Phone as PhoneIcon, FilterList as FilterListIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { Controller, useForm } from 'react-hook-form';
import LoadingScreen from '../components/common/LoadingScreen';
import { usePermissions } from '../hooks/usePermissions';

const generateSecret = (length = 12) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const COLUMN_OPTIONS = [
  { id: 'exten', label: 'Exten', sortable: true, filterable: true, type: 'string' },
  { id: 'extension_type', label: 'Tipo', sortable: true, filterable: true, type: 'string' },
  { id: 'context', label: 'Context', sortable: true, filterable: true, type: 'string' },
  { id: 'secret', label: 'Secret', sortable: true, filterable: true, type: 'string' },
  { id: 'allow', label: 'Codecs', sortable: true, filterable: true, type: 'string' },
  { id: 'qualify', label: 'Qualify', sortable: true, filterable: true, type: 'string' },
  { id: 'nat', label: 'NAT', sortable: true, filterable: true, type: 'string' },
  { id: 'description', label: 'Descripción', sortable: true, filterable: true, type: 'string' },
  { id: 'actions', label: 'Acciones', sortable: false, filterable: false, type: 'string', align: 'right' }
];

export default function ExtensionsPage() {
  const { extensions } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();
  const [extList, setExtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExt, setEditingExt] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // Estados para creación masiva
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [randomSecret, setRandomSecret] = useState(true);
  const [bulkSecret, setBulkSecret] = useState('');
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);

  // Default values for WebRTC
  const { register, handleSubmit, reset, control, formState: { errors }, watch } = useForm({
    defaultValues: {
        exten: '',
        secret: '',
        context: 'Local',
        extension_type: 'WEBRTC', // Default to WebRTC
        dtmfmode: 'rfc2833',
        nat: true,
        invite: false,
        canreinvite: false,
        allow: ['alaw'], // Default codec selection
        description: '',
        defaultuser: '',
        username: '',
        dtlsenable: true,
        dtlsverify: false,
        dtlsrekey: 0,
        dtlscertfile: '/etc/asterisk/keys/asterisk.pem',
        dtlsprivatekey: '/etc/asterisk/keys/asterisk.pem',
        dtlssetup: 'actpass'
    }
  });

  const handleSortChange = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const getColumnFilterString = (row, column) => {
    switch (column) {
      case 'exten':
        return row.exten || '';
      case 'extension_type':
        return row.extension_type || 'SIP';
      case 'context':
        return row.context || '';
      case 'secret':
        return row.secret || '';
      case 'allow':
        return Array.isArray(row.allow) ? row.allow.join(', ') : (row.allow || '');
      case 'qualify':
        return row.qualify || 'yes';
      case 'nat':
        return typeof row.nat === 'string' ? row.nat : (row.nat ? 'yes' : 'no');
      case 'description':
        return row.description || '';
      default:
        return '';
    }
  };

  const columnFilteredList = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .filter(([column, value]) => COLUMN_OPTIONS.some(opt => opt.id === column && opt.filterable) && value?.trim());
    if (!activeFilters.length) {
      return extList;
    }
    return extList.filter(row =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getColumnFilterString(row, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [columnFilters, extList]);

  const sortedList = useMemo(() => {
    if (!sortConfig.column) {
      return columnFilteredList;
    }
    const columnType = COLUMN_OPTIONS.find(opt => opt.id === sortConfig.column)?.type || 'string';
    const data = [...columnFilteredList];
    const getSortValue = (row) => {
      switch (sortConfig.column) {
        case 'exten':
          return row.exten || '';
        case 'extension_type':
          return row.extension_type || '';
        case 'context':
          return row.context || '';
        case 'secret':
          return row.secret || '';
        case 'allow':
          return Array.isArray(row.allow) ? row.allow.join(', ') : (row.allow || '');
        case 'qualify':
          return row.qualify || '';
        case 'nat':
          return typeof row.nat === 'string' ? row.nat : (row.nat ? 'yes' : 'no');
        case 'description':
          return row.description || '';
        default:
          return '';
      }
    };
    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (columnType === 'number') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        return sortConfig.direction === 'asc' ? diff : -diff;
      }
      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return sortConfig.direction === 'asc' ? -1 : 1;
    });
    return data;
  }, [columnFilteredList, sortConfig]);

  const extensionType = watch('extension_type');
  const watchedExten = watch('exten');

  useEffect(() => { loadData(); }, []);


  const loadData = async () => {
    try {
      setLoading(true);
      const res = await extensions.getAll();
      setExtList(res?.data?.extensions ?? []);
    } catch (error) {
      showError('Error al cargar extensiones');
      console.error('Error loading extensions:', error);
      setExtList([]);
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = (ext = null) => {
    setEditingExt(ext);

    if (ext) {
        const allowArr = Array.isArray(ext.allow) ? ext.allow : (ext.allow || '').split(',').map(s=>s.trim()).filter(Boolean);
        const extType = ext.extension_type || 'SIP';
        reset({
          exten: ext.exten || '',
          secret: ext.secret || '',
          context: ext.context || 'Local',
          extension_type: extType,
          dtmfmode: ext.dtmfmode || (extType === 'WEBRTC' ? 'rfc2833' : 'auto'),
          nat: ext.nat === 'yes' || ext.nat === true || ext.nat === 1,
          invite: ext.invite === 'yes' || ext.invite === true || ext.invite === 1,
          canreinvite: ext.canreinvite === 'yes' || ext.canreinvite === true || ext.canreinvite === 1,
          allow: allowArr.length ? allowArr : ['alaw'],
          description: ext.description || '',
          defaultuser: ext.defaultuser || ext.exten || '',
          username: ext.username || ext.exten || '',
          dtlsenable: ext.dtlsenable !== undefined ? ext.dtlsenable : true,
          dtlsverify: ext.dtlsverify !== undefined ? ext.dtlsverify : false,
          dtlsrekey: ext.dtlsrekey || 0,
          dtlscertfile: ext.dtlscertfile || '/etc/asterisk/keys/asterisk.pem',
          dtlsprivatekey: ext.dtlsprivatekey || '/etc/asterisk/keys/asterisk.pem',
          dtlssetup: ext.dtlssetup || 'actpass'
        });
    } else {
      reset({
        exten: '',
        secret: generateSecret(),
        context: 'Local',
        extension_type: 'WEBRTC',
        dtmfmode: 'rfc2833',
        nat: true,
        invite: false,
        canreinvite: false,
        allow: ['alaw'],
        description: '',
        defaultuser: '',
        username: '',
        dtlsenable: true,
        dtlsverify: false,
        dtlsrekey: 0,
        dtlscertfile: '/etc/asterisk/keys/asterisk.pem',
        dtlsprivatekey: '/etc/asterisk/keys/asterisk.pem',
        dtlssetup: 'actpass'
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExt(null);
    setIsBulkMode(false);
    setRangeStart('');
    setRangeEnd('');
    setRandomSecret(true);
    setBulkSecret('');
  };

  const onSubmit = async (form) => {
    if (submitting) return; // Prevenir doble envío

    try {
      setSubmitting(true);

      // Asterisk espera nat/invite/canreinvite como "yes"/"no"
        form.nat = form.nat ? 'yes' : 'no';
        form.invite = form.invite ? 'yes' : 'no';
        form.canreinvite = form.canreinvite ? 'yes' : 'no';
        if (!Array.isArray(form.allow)) form.allow = [form.allow].filter(Boolean);

      // Auto-asignar defaultuser y username con el número de extensión
      form.defaultuser = form.exten;
      form.username = form.exten;

      // RHF entrega allow como array si hay varios checkboxes con el mismo name
      if (!Array.isArray(form.allow)) {
        form.allow = [form.allow].filter(Boolean);
      }
      form.allow_opus = Array.isArray(form.allow) && form.allow.includes('opus') ? 'yes' : 'no';

      if (editingExt) {
        await extensions.update(editingExt.exten, form);
        showSuccess('Extensión actualizada correctamente');
      } else {
        await extensions.create(form);
        showSuccess('Extensión creada correctamente');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      showError('Error al guardar la extensión');
      console.error('Save extension error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteExt = async (exten) => {
    if (!window.confirm('¿Está seguro de eliminar esta extensión?')) return;
    if (deleting) return; // Prevenir múltiples eliminaciones

    try {
      setDeleting(true);
      await extensions.delete(exten);
      showSuccess('Extensión eliminada correctamente');
      loadData();
    } catch (error) {
      showError('Error al eliminar la extensión');
      console.error('Delete extension error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const onSubmitBulk = async (form) => {
    if (submitting) return;

    // Validar rango
    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);

    if (!rangeStart || !rangeEnd) {
      showError('Debe especificar el rango de inicio y fin');
      return;
    }

    if (isNaN(start) || isNaN(end) || start > end || start < 1) {
      showError('Rango inválido');
      return;
    }

    if ((end - start + 1) > 100) {
      showError('El rango no puede exceder 100 extensiones');
      return;
    }

    if (!randomSecret && !bulkSecret) {
      showError('Debe especificar un secret o activar generación aleatoria');
      return;
    }

    try {
      setSubmitting(true);

      // Preparar template con valores del formulario
      const template = {
        context: form.context,
        extension_type: form.extension_type,
        dtmfmode: form.dtmfmode,
        nat: form.nat ? 'yes' : 'no',
        invite: form.invite ? 'yes' : 'no',
        canreinvite: form.canreinvite ? 'yes' : 'no',
        allow: Array.isArray(form.allow) ? form.allow : [form.allow].filter(Boolean),
        description: form.description,
        dtlsenable: form.dtlsenable,
        dtlsverify: form.dtlsverify,
        dtlsrekey: form.dtlsrekey,
        dtlscertfile: form.dtlscertfile,
        dtlsprivatekey: form.dtlsprivatekey,
        dtlssetup: form.dtlssetup
      };

      if (!randomSecret && bulkSecret) {
        template.secret = bulkSecret;
      }

      const bulkData = {
        rangeStart: start,
        rangeEnd: end,
        template,
        randomSecret
      };

      const response = await extensions.createBulk(bulkData);
      showSuccess(response.message || `Se crearon ${response.count} extensiones exitosamente`);
      handleCloseDialog();
      loadData();
    } catch (error) {
      showError(error.message || 'Error al crear extensiones masivamente');
      console.error('Bulk extension creation error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Extensiones</Typography>
        <ButtonGroup variant="contained">
          {canAction('create_extension') && (
            <Button startIcon={<AddIcon />} onClick={() => { setIsBulkMode(false); handleOpenDialog(null); }}>
              Nueva Extensión
            </Button>
          )}
          {canAction('create_bulk_extensions') && (
            <Button
              size="small"
              onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            >
              <ArrowDropDownIcon />
            </Button>
          )}
        </ButtonGroup>
      </Box>

      {/* Menú desplegable para opciones de creación */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setIsBulkMode(false); handleOpenDialog(null); setCreateMenuAnchor(null); }}>
          Crear Individual
        </MenuItem>
        <MenuItem onClick={() => { setIsBulkMode(true); handleOpenDialog(null); setCreateMenuAnchor(null); }}>
          Crear Masivo (Rango)
        </MenuItem>
      </Menu>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={3} alignItems="center">
            <PhoneIcon fontSize="small" />
            <Typography variant="body2">Total: {extList.length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMN_OPTIONS.map((column) => (
                <TableCell key={column.id} align={column.align || 'left'}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortConfig.column === column.id}
                          direction={sortConfig.column === column.id ? sortConfig.direction : 'asc'}
                          onClick={() => handleSortChange(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        <Typography variant="subtitle2">{column.label}</Typography>
                      )}
                      {column.filterable && (
                        <IconButton
                          size="small"
                          color={columnFilters[column.id] ? 'primary' : 'default'}
                          onClick={() =>
                            setActiveFilterColumn(prev => (prev === column.id ? null : column.id))
                          }
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {column.filterable && activeFilterColumn === column.id && (
                      <TextField
                        value={columnFilters[column.id] || ''}
                        onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
                        size="small"
                        placeholder="Filtrar"
                        variant="standard"
                        autoFocus
                        onBlur={() => setActiveFilterColumn(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            setActiveFilterColumn(null);
                          }
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedList.map((ext) => (
              <TableRow key={ext.exten} hover>
                <TableCell>{ext.exten}</TableCell>
                <TableCell>{ext.extension_type || 'SIP'}</TableCell>
                <TableCell>{ext.context}</TableCell>
                <TableCell>{ext.secret}</TableCell>
                <TableCell>{Array.isArray(ext.allow) ? ext.allow.join(', ') : (ext.allow || '')}</TableCell>
                <TableCell>{ext.qualify || 'yes'}</TableCell>
                <TableCell>{typeof ext.nat === 'string' ? ext.nat : (ext.nat ? 'yes' : 'no')}</TableCell>
                <TableCell>{ext.description || '-'}</TableCell>
                <TableCell align="right">
                  {canAction('edit_extension') && (
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(ext)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canAction('delete_extension') && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDeleteExt(ext.exten)} disabled={deleting}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sortedList.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} align="center">No hay extensiones.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingExt ? 'Editar Extensión' : (isBulkMode ? 'Crear Extensiones Masivamente' : 'Nueva Extensión')}
        </DialogTitle>
        <DialogContent dividers>
          {/* Tipo de extensión */}
          <Box mb={3}>
            <Controller
              name="extension_type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Tipo de Extensión</InputLabel>
                  <Select {...field} label="Tipo de Extensión">
                    <MenuItem value="WEBRTC">WebRTC</MenuItem>
                    <MenuItem value="SIP">SIP Clásico</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          {/* Modo Masivo: Campos de rango */}
          {isBulkMode && !editingExt && (
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Rango de Extensiones
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                <TextField
                  label="Desde"
                  type="number"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  placeholder="5601"
                  required
                />
                <TextField
                  label="Hasta"
                  type="number"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  placeholder="5620"
                  required
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom fontWeight={600} mt={2}>
                Configuración de Secret
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={randomSecret}
                    onChange={(e) => {
                      setRandomSecret(e.target.checked);
                      if (e.target.checked) setBulkSecret('');
                    }}
                  />
                }
                label="Generar secret aleatorio para cada extensión"
              />
              {!randomSecret && (
                <TextField
                  label="Secret fijo para todas"
                  fullWidth
                  value={bulkSecret}
                  onChange={(e) => setBulkSecret(e.target.value)}
                  placeholder="Ingrese secret único"
                  sx={{ mt: 1 }}
                  required
                />
              )}
            </Box>
          )}

          {/* Modo Individual: Campos básicos */}
          {!isBulkMode && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
              <TextField
                label="Número"
                {...register('exten', { required: 'Requerido' })}
                error={!!errors.exten}
                helperText={errors.exten?.message}
              />
              <TextField
                label="Secret"
                {...register('secret', { required: 'Requerido' })}
                error={!!errors.secret}
                helperText={errors.secret?.message}
              />
            </Box>
          )}

          {/* Campos comunes */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
            <TextField label="Context" {...register('context')} />
            <TextField
              label="DTMF Mode"
              {...register('dtmfmode')}
              value={extensionType === 'WEBRTC' ? 'rfc2833' : 'auto'}
            />
          </Box>

          {/* Campos específicos para WebRTC */}
          {extensionType === 'WEBRTC' && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
              <TextField label="DTLS Cert File" {...register('dtlscertfile')} />
              <TextField label="DTLS Private Key" {...register('dtlsprivatekey')} />
              <TextField label="DTLS Setup" {...register('dtlssetup')} />
              <TextField
                label="DTLS Rekey (segundos)"
                type="number"
                {...register('dtlsrekey')}
              />
            </Box>
          )}

          <TextField
            label="Descripción"
            multiline
            rows={3}
            fullWidth
            sx={{ mb: 3 }}
            {...register('description')}
          />

          {/* Configuración según tipo */}
          <Box display="flex" gap={4}>
            {/* Columna izquierda: Configuraciones generales */}
            <Box>
              <FormLabel component="legend">Configuración General</FormLabel>
              <FormGroup>
                {extensionType === 'WEBRTC' ? (
                  <>
                    <Controller
                      name="dtlsenable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="DTLS Enable"
                        />
                      )}
                    />
                    <Controller
                      name="dtlsverify"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="DTLS Verify"
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      name="nat"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="NAT"
                        />
                      )}
                    />
                    <Controller
                      name="invite"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="Invite"
                        />
                      )}
                    />
                    <Controller
                      name="canreinvite"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                          label="Canreinvite"
                        />
                      )}
                    />
                  </>
                )}
              </FormGroup>
            </Box>

            {/* Columna derecha: Codecs */}
            <Box>
              <FormLabel component="legend">Codecs</FormLabel>
              <FormGroup>
                <Controller
                  name="allow"
                  control={control}
                  render={({ field }) => {
                    const arr = Array.isArray(field.value) ? field.value : [];
                    const toggle = (val) => {
                      const has = arr.includes(val);
                      const next = has ? arr.filter(v => v !== val) : [...arr, val];
                      field.onChange(next);
                    };
                    const checked = (val) => arr.includes(val);
                    return (
                      <>
                        {extensionType === 'WEBRTC' && (
                          <FormControlLabel
                            control={<Checkbox checked={checked('opus')} onChange={() => toggle('opus')} />}
                            label="opus"
                          />
                        )}
                        <FormControlLabel
                          control={<Checkbox checked={checked('ulaw')} onChange={() => toggle('ulaw')} />}
                          label="ulaw"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={checked('alaw')} onChange={() => toggle('alaw')} />}
                          label="alaw"
                        />
                        {extensionType === 'SIP' && (
                          <>
                            <FormControlLabel
                              control={<Checkbox checked={checked('gsm')} onChange={() => toggle('gsm')} />}
                              label="gsm"
                            />
                            <FormControlLabel
                              control={<Checkbox checked={checked('g729')} onChange={() => toggle('g729')} />}
                              label="g729"
                            />
                          </>
                        )}
                      </>
                    );
                  }}
                />
              </FormGroup>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={isBulkMode && !editingExt ? handleSubmit(onSubmitBulk) : handleSubmit(onSubmit)}
            disabled={submitting}
          >
            {submitting ? 'Procesando...' : (editingExt ? 'Guardar cambios' : (isBulkMode ? 'Crear Extensiones' : 'Crear'))}
          </Button>
        </DialogActions>
      </Dialog>


      {(submitting || deleting) && (
        <LoadingScreen
          message={submitting ? 'Guardando extensión...' : 'Eliminando extensión...'}
        />
      )}
    </Box>
  );
}
